import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { TrendingUp, MapPin, FileCheck, Users, ExternalLink, Clock, Newspaper } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import JourneyStep from "@/components/home/JourneyStep";

interface Article {
  title: string;
  link: string;
  pubDate: string;
  source: string;
}

const metrics = [
  { label: "AL Counties", value: "67", icon: MapPin },
  { label: "Bills Analyzed", value: "463", icon: FileCheck },
  { label: "AL Partners", value: "218+", icon: Users },
  { label: "Policy Wins", value: "34", icon: TrendingUp },
];

const formatDate = (dateStr: string) => {
  try {
    return new Date(dateStr).toLocaleDateString("en-US", { month: "short", day: "numeric" });
  } catch {
    return "";
  }
};

const ProofSection = () => {
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data, error } = await supabase.functions.invoke("fetch-news");
        if (error) throw error;
        if (data?.success && data.articles) setArticles(data.articles.slice(0, 4));
      } catch (err) {
        console.error("ProofSection: fetch-news failed", err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <JourneyStep
      step="05"
      label="Proof"
      tone="muted"
      title={<>How we know it's <span className="text-gradient-primary">working</span></>}
      subhead="Statewide reach plus the community signals showing momentum on the ground."
    >
      <div className="grid lg:grid-cols-[1fr_1.1fr] gap-10 items-start">
        {/* Stats */}
        <div className="grid grid-cols-2 gap-5">
          {metrics.map((m, i) => (
            <motion.div
              key={m.label}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.08 }}
              className="rounded-[var(--radius)] border border-border bg-card/60 p-5"
            >
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center mb-3">
                <m.icon className="h-5 w-5 text-primary" />
              </div>
              <div className="font-display text-3xl md:text-4xl font-bold mb-1">{m.value}</div>
              <div className="text-xs text-muted-foreground uppercase tracking-wider">{m.label}</div>
            </motion.div>
          ))}
        </div>

        {/* News list */}
        <div>
          <div className="flex items-center gap-2 mb-4">
            <Newspaper className="h-4 w-4 text-primary" />
            <span className="text-xs font-semibold text-primary uppercase tracking-wider">
              Community Spotlight
            </span>
          </div>
          <div className="space-y-3">
            {loading
              ? Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="rounded-[var(--radius)] border border-border bg-card/40 p-4 animate-pulse">
                    <div className="h-3 w-16 bg-muted rounded mb-3" />
                    <div className="h-4 w-3/4 bg-muted rounded mb-2" />
                    <div className="h-3 w-1/3 bg-muted rounded" />
                  </div>
                ))
              : articles.length === 0
              ? <p className="text-sm text-muted-foreground">No recent articles available.</p>
              : articles.map((a, i) => (
                  <motion.a
                    key={i}
                    href={a.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    initial={{ opacity: 0, x: 10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.06 }}
                    className="group block rounded-[var(--radius)] border border-border bg-card/60 hover:bg-card hover:border-primary/40 transition-all p-4"
                  >
                    <div className="flex items-center justify-between mb-2 text-[10px] uppercase tracking-wider">
                      <span className="text-primary/80 font-semibold">{a.source}</span>
                      <span className="flex items-center gap-1 text-muted-foreground">
                        <Clock className="h-3 w-3" /> {formatDate(a.pubDate)}
                      </span>
                    </div>
                    <div className="flex items-start justify-between gap-3">
                      <h3 className="font-display text-sm font-semibold leading-snug group-hover:text-primary transition-colors line-clamp-2">
                        {a.title}
                      </h3>
                      <ExternalLink className="h-3.5 w-3.5 text-muted-foreground group-hover:text-primary transition-colors shrink-0 mt-0.5" />
                    </div>
                  </motion.a>
                ))}
          </div>
        </div>
      </div>
    </JourneyStep>
  );
};

export default ProofSection;